import {Handle, Position, useEdges, useNodeId, useNodes, useReactFlow} from "@xyflow/react";
import {FaExternalLinkAlt, FaSearchPlus, FaTrash} from "react-icons/fa";
import {FaCircleNodes, FaWandMagicSparkles} from "react-icons/fa6";
import {useEdgeBuilder} from "../../association-network-context.jsx";
import {useAnalysis} from "../../hooks/use-analysis.js";
import {getLayoutedElements} from "../utils.js";
import I18nKeys from "../i18n-keys.js";
import {Types} from "../enums.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import {toast} from "react-toastify";
import {useEffect} from "react";


const BaseNode = ({
      icon,
      title,
      image,
      infoBlock,
      onSearchConnections,
      onExtraAction,
      onSearchPassage,
      showAddConnectionButton = false,
      className = "",
      selected,
      bgHeader,
      bgBody,
      infoPassage,
      onExtraActionDisabled,
      type,
      width = "w-[130px]"
  }) => {
    const {setDeletedAttach} = useEdgeBuilder();
    const nodeId = useNodeId();
    const nodes = useNodes();
    const edges = useEdges();
    const {addEdges, setNodes, setEdges} = useReactFlow();
    const {
        newEdge,
        reset,
        setPanelOpened,
        toggleItemsNode,
        setSelectedNode,
        setIsLoading,
        setShowCancelRequestBtn
    } = useEdgeBuilder();

    const {runAnalysis} = useAnalysis();

    const openDetails = (e) => {
        if (e.ctrlKey) return;

        if (newEdge) {
            newConnection();
            return;
        }

        const nodeFind = nodes.find((n) => n.id === nodeId);
        if (nodeFind) {
            setSelectedNode(nodeFind);
            setPanelOpened("details")
        }
    }

    const newConnection = () => {
        if (newEdge) {
            addEdges({
                ...newEdge,
                id: `e${newEdge.source}-${nodeId}`,
                target: nodeId,
            });
            reset();
        } else {
            setPanelOpened("addEdge");
        }
    }

    const getIdFromNode = (node) => {
        const {type, data} = node;
        const {id, cpf, plate} = data;
        switch (type) {
            case "individual":
                return cpf;
            case "occurrence":
            case "bo":
            case "sos":
            case "encounter":
                return id
            case "vehicle":
            case "passage":
                return plate
        }
    }

    const expandNodeConnections = async () => {
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return;

        try {
            setIsLoading(true);
            setShowCancelRequestBtn(false);

            const formData = new FormData()

            formData.append("type", node.type)
            formData.append("value", getIdFromNode(node))

            const data = await runAnalysis(formData);
            if (!data) return;

            if (data.edges.length === 0 || data.nodes.length === 0) {
                toast.warning("Nenhuma associação encontrada.");
                return
            }

            const newEdges = data.edges.filter(d => !edges.some(n => n.id === d.id));
            const newNodes = data.nodes.filter(d => !nodes.some(n => n.id === d.id));

            const layoutedNodes = getLayoutedElements({
                newNodes,
                newEdges,
                fixedNode: node,
                existingNodes: nodes,
            });

            setNodes((prev) => [...prev, ...layoutedNodes]);
            setEdges((prev) => [...prev, ...newEdges]);
        } catch (err) {
            console.error(err);
            const {message} = err.response.data;

            let errMessage = "Erro ao expandir conexões.";
            if (I18nKeys[message]) {
                errMessage = I18nKeys[message]
                switch (node.type) {
                    case Types.INDIVIDUAL: errMessage = errMessage.replaceAll(":type", "CPF de interesse"); break;
                    case Types.VEHICLE: errMessage = errMessage.replaceAll(":type", "Placa de interesse"); break;
                    default: errMessage = errMessage.replaceAll(":type", "Identificador"); break;
                }
            }
            projectUtils.swalErrorMessage("", errMessage, "Ok");
        } finally {
            setIsLoading(false);
        }
    }

    const onDelete = () => {
        const node = nodes.find(n => n.id === nodeId);
        if (node) {
            if (node.attach_id) setDeletedAttach(prev => ([...prev, node.attach_id]))
        }

        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    }

    return (
        <div
            className={`relative group ${className} ${selected ? "border border-dashed! border-red-500! rounded-[5px]" : ""}`}>
            <>
                <Handle type="target" position={Position.Top} id="t-top" hidden={true}/>
                <Handle type="target" position={Position.Right} id="t-right" hidden={true}/>
                <Handle type="target" position={Position.Bottom} id="t-bottom" hidden={true}/>
                <Handle type="target" position={Position.Left} id="t-left" hidden={true}/>

                <Handle type="source" position={Position.Bottom} id="s-bottom" hidden={true}/>
                <Handle type="source" position={Position.Top} id="s-top" hidden={true}/>
                <Handle type="source" position={Position.Right} id="s-right" hidden={true}/>
                <Handle type="source" position={Position.Left} id="s-left" hidden={true}/>
            </>

            {
                newEdge && newEdge.source === nodeId &&
                <div className={"h-full bg-[#cfcfcfa1] absolute z-[9999] w-full bottom-0"}></div>
            }

            <div
                className={`${(newEdge && newEdge.source !== nodeId) ? "!border-[#ff0000]" : "!border-[#cfcfcf]"} relative ${bgBody} !border rounded shadow ${width}`}
                onClick={openDetails}
            >
                <div
                    className={`text-white flex items-center rounded-t-[3px] ${type !== Types.VEHICLE ? "p-[3px]" : ""} ${bgHeader} ${toggleItemsNode.compact.compact ? "rounded-b-[3px]" : ""}`}>
                    {type !== Types.VEHICLE && (
                        <>
                            {icon && <div className="w-[25px] flex justify-center">{icon}</div>}
                            <div className="text-[8px] leading-[10px] w-[calc(100%-25px)]">{title}</div>
                        </>
                    )}

                    {type === Types.VEHICLE && <>{title}</>}
                </div>

                {!toggleItemsNode.compact.compact && (
                    <div className={"max-h-[110px] overflow-hidden"}>
                        {
                            image &&
                            <>
                                {(![Types.PASSAGE, Types.VEHICLE].includes(type)) && (
                                    <div className="h-[50px] border-top !border-[#cfcfcf] bg-[#f3f3f4]">
                                        <img className="m-auto h-full w-full object-contain p-[3px]" src={image} alt="image"/>
                                    </div>
                                )}

                                {[Types.PASSAGE, Types.VEHICLE].includes(type) && <>{image}</>}
                            </>
                        }
                        {infoBlock}
                    </div>
                )}
            </div>

            <div className="hidden absolute group-hover:block">
                <div className="flex mt-[2px]">
                    <div
                        onClick={e => {
                            e.stopPropagation()
                            onDelete()
                        }}
                        title={"Remover item"}
                        className="cursor-pointer w-[18px] h-[18px] flex items-center justify-center !border !border-[#cfcfcf] bg-[#dbdbdb] rounded hover:bg-[#ccc]">
                        <FaTrash size={8} color="#333"/>
                    </div>
                    {onSearchConnections && (
                        <div
                            onClick={e => {
                                e.stopPropagation()
                                expandNodeConnections()
                            }}
                            title={"Pesquisar associações"}
                            className="ml-1 cursor-pointer w-[18px] h-[18px] flex items-center justify-center !border !border-[#cfcfcf] bg-[#dbdbdb] rounded hover:bg-[#ccc]">
                            <FaSearchPlus size={8} color="#333"/>
                        </div>
                    )}
                    {onSearchPassage && (
                        <div
                            onClick={e => {
                                e.stopPropagation()
                                onSearchPassage()
                            }}
                            title={"Analisar passagens coincidentes"}
                            className="ml-1 cursor-pointer w-[18px] h-[18px] flex items-center justify-center !border !border-[#cfcfcf] bg-[#dbdbdb] rounded hover:bg-[#ccc]"
                        >
                            <FaWandMagicSparkles size={8} color="#333"/>
                        </div>
                    )}
                    {onExtraAction && (
                        <div
                            onClick={e => {
                                e.stopPropagation()
                                onExtraAction();
                            }}
                            title="Abrir"
                            className={`ml-1 w-[18px] h-[18px] flex items-center justify-center !border !border-[#cfcfcf] bg-[#dbdbdb] rounded hover:bg-[#ccc] ${onExtraActionDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            <FaExternalLinkAlt size={8} color="#333"/>
                        </div>
                    )}
                    {showAddConnectionButton && (
                        <div
                            onClick={newConnection}
                            title="Adicionar conexão"
                            className="ml-1 cursor-pointer w-[18px] h-[18px] flex items-center justify-center !border !border-[#cfcfcf] bg-[#dbdbdb] rounded hover:bg-[#ccc]">
                            <FaCircleNodes size={8} color="#333"/>
                        </div>
                    )}

                    {infoPassage}
                </div>
            </div>
        </div>
    );
};

export default BaseNode;
